import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Typography, Box } from '@material-ui/core';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from 'material-ui/FlatButton';
import Formsy from 'formsy-react';
import { FormsyText } from 'formsy-material-ui/lib';
import ApiSelector from '../../../containers/common/selectors/selectorContainer';
import Table from '../../../containers/common/tables/tableContainer';
import NonBlockingLoader from '../../../components/common/Loading/NonBlockingLoader';
import {
  teamsEndpoint,
  treatmentsByReferralIdTable,
  referralSourcesEndpoint,
  referralServiceEndpoint,
  locationTypesEndpoint,
  referenceCodesEndpoint,
  referralPrioritiesEndpoint,
} from '../../../settings/constants';
import { refSetter } from '../../../utils/helpers';
import { ExternalProviderType, treatmentStatusOptions } from '../../../enums';
import globals from '../../../globals/communityGlobals';
import { textFieldStyle } from '../../../theme/AllocateBaseTheme';
import ReferralCloseDialog from '../../../containers/Referrals/ReferralCloseDialog/ReferralCloseDialogContainer';
import { clearBorderValues, extractDateOnly, getDateForApi, isValidDate } from '../../../utils/dates';
import { Address } from '../../../propTypes/entities/address';
import ReferralTreatmentsFilter from './ReferralTreatmentsFilter';
import { getErrorProps, checkRequiredOnBlur } from '../../../utils/keyboardSelectorFunctions';
import KeyboardDateSelector from '../../common/KeyboardDateTimeSelectors/KeyboardDateSelector';

const locale = globals.getArea('referrals');
const localeForTreatments = globals.getArea('treatments');
const errors = globals.getArea('error');

const columns = [
  {
    Header: locale.getMsg('communityMatron'),
    id: 'cm',
    width: 50,
    Cell: row => <div style={{ textAlign: 'center' }}>{row.cm ? '✔' : '-'}</div>,
  },
  {
    Header: locale.getMsg('emergencyUnscheduled'),
    id: 'emergencyUnscheduled',
    width: 100,
    Cell: row => <div style={{ textAlign: 'center' }}>{row.emergencyUnscheduled ? 'yes' : 'no'}</div>,
  },
  {
    Header: locale.getMsg('activities'),
    id: 'activities',
    accessor: row => <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.activities}</span>,
    minWidth: 250,
  },
  {
    Header: locale.getMsg('orgUnits'),
    accessor: 'orgUnits',
    minWidth: 200,
  },
  {
    Header: locale.getMsg('validFrom'),
    id: 'validFrom',
    accessor: row => clearBorderValues(row.dateFrom),
    width: 100,
  },
  {
    Header: locale.getMsg('validTo'),
    id: 'validTo',
    accessor: row => clearBorderValues(row.isRetrospectiveVisit ? row.dateFrom : row.dateTo),
    width: 100,
  },
  {
    Header: locale.getMsg('status'),
    id: 'status',
    width: 80,
    accessor: row => treatmentStatusOptions.find(s => s.id === row.status).name,
  },
  {
    Header: locale.getMsg('isRetroVisit'),
    id: 'isRetro',
    accessor: row => (row.isRetrospectiveVisit ? '✔' : '-'),
    style: { paddingLeft: '20px' },
    width: 80,
  },
  {
    Header: locale.getMsg('dayWeek'),
    accessor: 'dayWeek',
  },
  {
    Header: locale.getMsg('frequency'),
    accessor: 'frequency',
    width: 100,
  },
];

const defaultTreatmentFilters = {
  treatmentStatuses: [1],
  orgUnits: [0],
  activities: [0],
  dateFrom: new Date(globals.now),
  dateTo: null,
};

class ReferralDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      canSave: null,
      showWillAffectVisitsWarning: false,
      newPasReferralIdValue: null,
      chars_left: 256,
      max_chars: 256,
      ...defaultTreatmentFilters,
      filterParams: {},
      referralReceivedDate: props.referral.dateReceived ? new Date(props.referral.dateReceived) : null,
    };
  }

  onTreatmentsFilterPropsChange = (prop, value) => {
    this.setState({ [prop]: value });
  };

  setValidationErrors = newValidationErrors => {
    this.setState({
      validationErrors: newValidationErrors,
    });
  };

  setRef = refSetter.bind(this);

  setValidity = value => () => {
    this.setState({
      canSave: value,
    });
  };

  getCharLeftValue = () => {
    return this.state.newPasReferralIdValue === null && this.props.referral.pasReferralId != null
      ? this.state.max_chars - this.props.referral.pasReferralId.length
      : this.state.chars_left;
  };

  handleTreatmentsFilterSubmit = activityNames => {
    const { treatmentStatuses, orgUnits, dateFrom, dateTo } = this.state;
    this.setState({
      filterParams: {
        treatmentStatuses: treatmentStatuses.filter(id => id !== 0),
        orgUnits: orgUnits.filter(id => id !== 0),
        activities: activityNames ? activityNames.map(name => encodeURIComponent(name)) : [],
        dateFrom: getDateForApi(dateFrom),
        dateTo: getDateForApi(dateTo),
      },
    });
  };

  handleTreatmentsFilterClear = () => {
    this.setState({
      filterParams: {
        ...defaultTreatmentFilters,
        dateFrom: getDateForApi(defaultTreatmentFilters.dateFrom),
        dateTo: getDateForApi(defaultTreatmentFilters.dateTo),
      },
      ...defaultTreatmentFilters,
    });
  };

  handleNavigate = url => () => {
    this.props.navigate(url);
  };

  handleSave = model => {
    model.referralReceived = this.state.referralReceivedDate;
    this.props.handleSave(model);
  };

  handleClear = () => {
    this.form.reset();
  };

  addCurrentValue = value => options => {
    if (value === undefined || value === null) {
      return options;
    }
    if (options) {
      if (!options.find(x => x.id === value.id)) {
        options.push({ id: value.id, name: value.name });
      }
    }
    return options;
  };

  handleChange = () => {
    if (this.state.showWillAffectVisitsWarning !== this.props.referral.treatmentsHaveAssignedFutureTreatmentInstances) {
      this.setState({
        showWillAffectVisitsWarning: this.props.referral.treatmentsHaveAssignedFutureTreatmentInstances,
      });
    }
  };

  updatePasReferralIdValue = (event, value) => {
    this.setState({ newPasReferralIdValue: value });
    const charCount = event.target.value.length;
    const maxChar = this.state.max_chars;
    const charLength = maxChar - charCount;
    this.setState({ chars_left: charLength });
  };

  isReferralDateBeforeTreatments = (values, referralReceived) => {
    const referralTreatments = values.referralTreatments;

    if (
      typeof referralReceived === 'undefined' ||
      !isValidDate(referralReceived) ||
      typeof referralTreatments === 'undefined' ||
      referralTreatments.length < 1
    ) {
      return true;
    }

    return !referralTreatments.some(treatment => moment(referralReceived).isAfter(treatment.dateFrom, 'day'));
  };

  removeMsgAndResetBtn = () => {
    setTimeout(() => this.props.resetSyncStatusMsg(), 5000);
  };

  noValidationErrors = () => {
    return !this.state.validationErrors || !this.state.validationErrors.referralReceivedDate;
  };

  isReferralReceivedDateValid = () => {
    const { referralReceivedDate } = this.state;
    return isValidDate(referralReceivedDate) && this.noValidationErrors();
  };

  handleDateChange = (fieldName, date) => {
    let errorMsg = null;
    if (!date) errorMsg = errors.getMsg('requiredFieldValidationMessage');
    else {
      const values = this.form.getCurrentValues();
      date = extractDateOnly(date);
      if (!this.isReferralDateBeforeTreatments(values, date)) {
        errorMsg = errors.getMsg('cannotUpdateReferralReceivedDate');
      }
    }
    this.setState(prevState => ({
      validationErrors: { ...prevState.validationErrors, [fieldName]: errorMsg },
      [fieldName]: date,
    }));
  };

  render() {
    const {
      readOnly,
      referralId,
      navigate,
      referral,
      showTreatments,
      showClose,
      canAddTreatments,
      canEditTreatments,
      canEdit,
      isImported,
      referralTreatments,
      externalProviderType,
      isFetchingComplete,
      messages,
      isSyncFetching,
      handleSyncTreatments,
    } = this.props;

    const formattedErrorMsg = () => {
      return messages.map(item => <p>{item}</p>);
    };

    const {
      canSave,
      showWillAffectVisitsWarning,
      treatmentStatuses,
      orgUnits,
      activities,
      dateFrom,
      dateTo,
      filterParams,
    } = this.state;
    return (
      <React.Fragment>
        <Box display="flex" flexDirection="column" px={2} py={1}>
          <Typography variant="h5" gutterBottom>
            {locale.getMsg('referralDetails')}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            {locale.getMsg('pleaseCompleteDescription')}
          </Typography>
        </Box>
        <Formsy.Form
          onValid={this.setValidity(true)}
          onInvalid={this.setValidity(false)}
          onSubmit={this.handleSave}
          onChange={this.handleChange}
          ref={this.setRef('form')}
        >
          <FormsyText name="referralTreatments" value={referralTreatments} disabled style={{ display: 'none' }} />
          <Box display="flex" p={1}>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <KeyboardDateSelector
                name="referralReceived"
                className="mui-date-picker"
                required
                label={locale.getMsg('referralReceived')}
                value={this.state.referralReceivedDate}
                disabled={readOnly || !canEdit || isImported}
                onChange={date => this.handleDateChange('referralReceivedDate', date)}
                onBlur={event =>
                  checkRequiredOnBlur(
                    event.target.value,
                    this.setValidationErrors,
                    'referralReceivedDate',
                    this.state.validationErrors,
                  )
                }
                errorProps={
                  this.state.validationErrors ? getErrorProps(this.state.validationErrors.referralReceivedDate) : null
                }
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <ApiSelector
                disabled={readOnly || !canEdit || isImported}
                name="referralPriorityId"
                endpoint={referralPrioritiesEndpoint}
                reduxIdentifier="referralPriority"
                required
                value={referral.referralPriority}
                floatingLabelText={`${locale.getMsg('referralPriority')} *`}
                isFetchRequired
                mapOptions={this.addCurrentValue(referral.referralPriority)}
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <ApiSelector
                disabled={readOnly || !canEdit || isImported}
                name="referralSourceId"
                endpoint={referralSourcesEndpoint}
                reduxIdentifier="referralSource"
                required
                value={referral.referralSource}
                floatingLabelText={`${locale.getMsg('referralSource')} *`}
                isFetchRequired
                mapOptions={this.addCurrentValue(referral.referralSource)}
              />
            </Box>
          </Box>
          <Box display="flex" p={1}>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <ApiSelector
                endpoint={teamsEndpoint}
                reduxIdentifier="team"
                required
                disabled={readOnly || !canEdit || isImported}
                floatingLabelText={`${locale.getMsg('orgUnits')}*`}
                name="allocatedToTeams"
                isFetchRequired
                value={referral.orgUnit}
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <ApiSelector
                disabled={readOnly || !canEdit}
                name="locationTypeId"
                endpoint={locationTypesEndpoint}
                reduxIdentifier="locationType"
                value={referral.locationType}
                floatingLabelText={`${locale.getMsg('locationType')}`}
                isFetchRequired
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <ApiSelector
                disabled={readOnly || !canEdit}
                name="referenceCodeId"
                endpoint={referenceCodesEndpoint}
                reduxIdentifier="referenceCode"
                value={referral.referenceCode}
                floatingLabelText={`${locale.getMsg('referenceCode')}`}
                isFetchRequired
              />
            </Box>
          </Box>
          <Box display="flex" p={1}>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <ApiSelector
                disabled={readOnly || !canEdit}
                name="referralServiceId"
                endpoint={referralServiceEndpoint}
                reduxIdentifier="referralService"
                value={referral.referralService}
                floatingLabelText={`${locale.getMsg('referralService')}`}
                isFetchRequired
                mapOptions={this.addCurrentValue(referral.referralService)}
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '66.6%' }}>
              <FormsyText
                name="pasReferralId"
                floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                style={textFieldStyle.style}
                inputStyle={textFieldStyle.inputStyle}
                underlineStyle={textFieldStyle.underlineStyle}
                floatingLabelText={locale.getMsg('pasReferralId')}
                value={referral.pasReferralId || ''}
                onChange={this.updatePasReferralIdValue}
                maxLength={this.state.max_chars}
              />
              <Typography variant="subtitle2">You have {this.getCharLeftValue()} characters left.</Typography>
            </Box>
          </Box>
          {!readOnly && (
            <Box p={1} flexGrow={1}>
              <Box p={1}>
                {showWillAffectVisitsWarning && (
                  <div className="row" style={{ marginLeft: '8px', color: '#e84c3d' }}>
                    <p>* {localeForTreatments.getMsg('willNotAffectAssignedVisits')}</p>
                  </div>
                )}
              </Box>
              <Box p={1}>
                {isImported && referral.unmappedProviderTeam && (
                  <div className="row" style={{ marginLeft: '8px', color: '#e84c3d' }}>
                    <p>{locale.getMsg('missingTeamWarning', [referral.unmappedProviderTeam])}</p>
                  </div>
                )}
              </Box>
              <Box display="flex" p={1}>
                <Box p={1}>
                  <FlatButton label={localeForTreatments.getMsg('clear')} onClick={this.handleClear} />
                </Box>
                <Box p={1}>
                  <RaisedButton
                    type="submit"
                    label={localeForTreatments.getMsg('save')}
                    disabled={!canSave || !canEdit || !this.isReferralReceivedDateValid()}
                    primary
                  />
                </Box>
                <Box p={1}>
                  {showClose && referralId && canEdit && !isImported && <ReferralCloseDialog referralId={referralId} />}
                </Box>
              </Box>
            </Box>
          )}
        </Formsy.Form>
        {showTreatments && (
          <React.Fragment>
            <Box display="flex" p={1}>
              <Box p={1} flexGrow={1}>
                <Typography variant="h5" gutterBottom>
                  {locale.getMsg('referralTreatments')}
                </Typography>
              </Box>
              <Box px={1}>
                <RaisedButton
                  onClick={this.handleNavigate(`/Patients/Patient/Referrals/${referralId}/Treatments/CreateRetroVisit`)}
                  disabled={readOnly || !referral.orgUnit}
                  label={locale.getMsg('completedVisit')}
                  primary
                />
              </Box>
              {isImported && externalProviderType === ExternalProviderType.SystmOne ? (
                <Box>
                  <RaisedButton
                    onClick={handleSyncTreatments}
                    label={locale.getMsg('syncTreatments')}
                    primary
                    disabled={readOnly || !referral.orgUnit || isSyncFetching}
                  />
                </Box>
              ) : (
                <Box display="flex">
                  {canAddTreatments === true ? (
                    <Box px={1}>
                      <RaisedButton
                        onClick={this.handleNavigate(`/Patients/Patient/Referrals/${referralId}/Treatments/Create`)}
                        disabled={readOnly || !referral.orgUnit}
                        label={locale.getMsg('addTreatment')}
                        primary
                      />
                    </Box>
                  ) : (
                    ''
                  )}
                </Box>
              )}
            </Box>

            <Box style={{ background: '#eef2f4', padding: '20px' }}>
              <ReferralTreatmentsFilter
                treatmentStatuses={treatmentStatuses}
                orgUnits={orgUnits}
                activities={activities}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onPropsChange={this.onTreatmentsFilterPropsChange}
                handleClear={this.handleTreatmentsFilterClear}
                handleTreatmentsFilterSubmit={this.handleTreatmentsFilterSubmit}
                filterOptions={this.props.filterOptions}
              />
            </Box>

            {referralId && (
              <Box display="flex" px={2} py={1}>
                <Box flexGrow={1}>
                  <Table
                    reduxIdentifier="referralTreatments"
                    endpoint={treatmentsByReferralIdTable}
                    columns={columns}
                    responseObjectName="treatments"
                    initialSearchParams={{
                      referralId,
                      pageNumber: 1,
                      pageSize: 30,
                      treatmentStatuses: [1],
                      orgUnits: [],
                      activities: [],
                      dateFrom: null,
                      dateTo: null,
                    }}
                    filterParams={filterParams}
                    rowOnClick={rowInfo => {
                      if (canEditTreatments === true) {
                        navigate(`/Treatments/${rowInfo.original.id}`);
                      }
                    }}
                    noDataText={locale.getMsg('noData')}
                    noDataComponent={<strong>{localeForTreatments.getMsg('noTreatmentsInTable')}</strong>}
                    triggerReload={isFetchingComplete === true}
                    afterReloadCallback={this.removeMsgAndResetBtn}
                  />
                </Box>
              </Box>
            )}
          </React.Fragment>
        )}
        {isFetchingComplete && <NonBlockingLoader message={formattedErrorMsg(messages)} autohideDuration={5000} />}
      </React.Fragment>
    );
  }
}

ReferralDetails.defaultProps = {
  showTreatments: false,
  areFieldsValid: false,
  readOnly: false,
  showClose: false,
  onFieldsUpdate: () => {},
  navigate: () => {},
  handleSave: () => {},
  handleSyncTreatments: () => {},
  resetSyncStatusMsg: () => {},
  referralId: null,
  referral: {
    dateReceived: undefined,
    locationType: undefined,
    orgUnits: undefined,
    referenceCode: undefined,
    referralPriority: undefined,
    referralSource: undefined,
    referralService: undefined,
    status: '',
  },
};

ReferralDetails.propTypes = {
  showTreatments: PropTypes.bool,
  referral: PropTypes.shape({
    status: PropTypes.string,
    dateReceived: PropTypes.string,
    id: PropTypes.number,
    locationType: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    orgUnits: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
    patient: PropTypes.shape({
      address: PropTypes.shape({
        addressLine1: PropTypes.string,
        addressLine2: PropTypes.string,
        addressLine3: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        postCode: PropTypes.string,
      }),
      patientFullName: PropTypes.string,
    }),
    referenceCode: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralPriority: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralSource: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralServce: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    treatments: PropTypes.arrayOf(
      PropTypes.shape({
        address: PropTypes.shape(Address),
        id: PropTypes.number,
        patientId: PropTypes.string,
        validFrom: PropTypes.string,
        validTo: PropTypes.string,
      }),
    ),
  }),
  readOnly: PropTypes.bool,
  showClose: PropTypes.bool,
  navigate: PropTypes.func,
  handleSave: PropTypes.func,
  handleSyncTreatments: PropTypes.func,
  resetSyncStatusMsg: PropTypes.func,
  referralId: PropTypes.number,
  canAddTreatments: PropTypes.bool.isRequired,
  canEditTreatments: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  isImported: PropTypes.bool.isRequired,
  // externalProviderType: PropTypes.number,
};

export default ReferralDetails;
